export const logisticsProduct = {
  slug: 'logistics',
  path: '/logistics',
  name: 'Logistics',
  shortName: 'Delivery Service',
  accent: 'teal',
  icon: 'Truck',
  whatsappText: 'Hi, I want to know about FastWhistle Logistics (per-parcel delivery service).',
  hero: {
    eyebrow: 'Managed Delivery Service',
    headline: 'Same-Day Pharma Delivery, Without Managing Your Own Fleet',
    highlight: null,
    subheadline:
      'Hand your parcels to FastWhistle. Our riders deliver to your retail stores, doctors, and stockists the same day — you pay per parcel, track in real time, and never worry about hiring or managing delivery staff.',
    badges: ['Per-parcel pricing', 'FastWhistle riders', 'Ahmedabad, Surat, Pune & Mumbai'],
    ctaPrimary: 'Start Delivery',
    ctaSecondary: 'WhatsApp Us',
    highlights: [
      { icon: 'Truck', label: 'FastWhistle riders deliver' },
      { icon: 'MapTrifold', label: 'Live parcel tracking' },
      { icon: 'CurrencyInr', label: 'Per-parcel pricing' },
    ],
    illustration: '/images/logistics/bike-delivery.webp',
    illustrationAlt: 'FastWhistle rider delivering a parcel to a retail pharmacy with live order tracking on the app',
  },
  model: {
    title: 'How the logistics model works',
    description:
      'You focus on distribution. We run the in-city delivery infrastructure — riders, routing, and real-time tracking.',
    points: [
      { icon: 'Package', title: 'You hand over parcels', detail: 'Distributor prepares orders/parcels and assigns them for delivery through FastWhistle.' },
      { icon: 'Truck', title: 'Our riders deliver', detail: 'FastWhistle infrastructure riders pick up and deliver same-day within the city and nearby areas.' },
      { icon: 'CurrencyInr', title: 'Pay per parcel', detail: 'Simple per-parcel delivery charges — no fleet salaries, vehicles, or HR overhead on your side.' },
      { icon: 'MapTrifold', title: 'Track every delivery', detail: 'See live status and delivery progress from your distributor view — full visibility without managing riders.' },
    ],
  },
  steps: [
    { step: '01', icon: 'Package', title: 'Hand Over Parcels', description: 'Distributor submits parcels/orders for delivery to retail stores, doctors, or stockists.' },
    { step: '02', icon: 'Truck', title: 'FastWhistle Dispatches', description: 'Our in-city rider network picks up and routes deliveries for same-day completion.' },
    { step: '03', icon: 'MapPin', title: 'Live Tracking', description: 'Track each delivery in real time — know when parcels are picked up, in transit, and delivered.' },
    { step: '04', icon: 'CheckCircle', title: 'Delivered Same Day', description: 'Parcels reach your customers the same day within Ahmedabad, Surat, Pune, Mumbai, and nearby areas.' },
    { step: '05', icon: 'Receipt', title: 'Clear Billing', description: 'Per-parcel delivery charges — transparent and simple, without fleet management costs.' },
  ],
  features: [
    { icon: 'Clock', title: 'Same-Day Delivery', benefit: 'In-city and nearby-city same-day delivery for medicine distributor parcels.' },
    { icon: 'MapTrifold', title: 'Realtime Tracking', benefit: 'Live delivery status so you always know where each parcel is.' },
    { icon: 'Truck', title: 'FastWhistle Riders', benefit: 'Our infrastructure — you do not hire, manage, or pay delivery staff directly.' },
    { icon: 'CurrencyInr', title: 'Per-Parcel Charges', benefit: 'Pay a basic amount per parcel delivery — predictable logistics cost.' },
    { icon: 'Buildings', title: 'Built for Distributors', benefit: 'Designed for medicine distributors delivering to chemists, doctors, and stockists.' },
    { icon: 'ShieldCheck', title: 'Secure Handoff', benefit: 'Proof of delivery and status updates for every parcel in the network.' },
  ],
  benefits: [
    'No fleet hiring or rider management on your side',
    'Same-day delivery to retail stores, doctors, and stockists',
    'Realtime visibility on every parcel',
    'Predictable per-parcel delivery pricing',
    'Ideal when you want logistics outsourced, not software to manage staff',
  ],
  faq: [
    {
      question: 'Do I need to manage riders for logistics?',
      answer: 'No. FastWhistle logistics is a managed delivery service. Our riders handle pickup and delivery. You hand over parcels and track them — you do not manage fleet staff for this service.',
    },
    {
      question: 'How is logistics priced?',
      answer: 'Logistics is priced per parcel delivery. You pay a basic delivery charge per parcel — no need to maintain your own delivery team for this service.',
    },
    {
      question: 'Which cities are covered for logistics?',
      answer: 'FastWhistle logistics (managed delivery) operates in Ahmedabad, Surat, Pune, and Mumbai, with same-day in-city and nearby-city delivery for medicine distributors.',
    },
    {
      question: 'Can I track deliveries in real time?',
      answer: 'Yes. You get real-time delivery tracking and status updates for parcels handled by FastWhistle riders.',
    },
    {
      question: 'Is this different from Fleet SaaS?',
      answer: 'Yes. Logistics is our managed delivery service with FastWhistle riders. Fleet SaaS is software for you to manage your own employees, tasks, and field activity. Many distributors use one or both.',
    },
  ],
};

export const fleetProduct = {
  slug: 'fleet-saas',
  path: '/fleet-saas',
  name: 'Fleet SaaS',
  shortName: 'Fleet Management',
  accent: 'blue',
  icon: 'DeviceMobile',
  whatsappText: 'Hi, I want a demo of FastWhistle Fleet Management SaaS for my distributor team.',
  hero: {
    eyebrow: 'Fleet Management SaaS',
    headline: 'Manage Your Field Team, Tasks & Activity — From One System',
    highlight: null,
    subheadline:
      'Give your salesmen and riders the fleet app. Assign order, collection, and calling tasks. Track live location, day-wise travel, attendance, leave, TADA, and productivity — all from your distributor panel.',
    badges: ['Your employees', 'Your data', 'Pan-India'],
    ctaPrimary: 'Book Fleet Demo',
    ctaSecondary: 'WhatsApp Us',
    highlights: [
      { icon: 'MapPin', label: 'Live location & routes' },
      { icon: 'Fingerprint', label: 'Attendance & leave' },
      { icon: 'ArrowsClockwise', label: 'Accounting sync' },
    ],
    illustration: '/images/fleet/fleet-tracking.webp',
    illustrationAlt: 'Admin monitoring live rider locations and routes on the FastWhistle fleet dashboard',
  },
  model: {
    title: 'How Fleet SaaS works',
    description:
      'You manage your own employees. FastWhistle gives you the software — fleet app for field staff and distributor panel for oversight.',
    points: [
      { icon: 'Users', title: 'Your employees use the fleet app', detail: 'Salesmen, riders, and field staff mark attendance and execute tasks from the mobile app.' },
      { icon: 'ListChecks', title: 'Assign tasks', detail: 'Order tasks, collection tasks, and calling tasks — all assigned and tracked from the distributor panel.' },
      { icon: 'MapTrifold', title: 'Live location & routes', detail: 'See on-field fleet location, current activity, and complete day-wise travel history.' },
      { icon: 'Desktop', title: 'Distributor panel control', detail: 'Overall tracking, reports, attendance, leave, TADA, and productivity from one dashboard.' },
    ],
  },
  steps: [
    { step: '01', icon: 'ArrowsClockwise', title: 'Sync Your Data', description: 'Parties, products, orders, and outstandings sync from ZeroMDS or your accounting software.' },
    { step: '02', icon: 'ListChecks', title: 'Assign Tasks', description: 'Create order, collection, or calling tasks for your fleet employees from the distributor panel.' },
    { step: '03', icon: 'DeviceMobile', title: 'Field Execution', description: 'Employees use the fleet app — attendance, deliveries, collections, and calls with activity logged.' },
    { step: '04', icon: 'MapPin', title: 'Track Live', description: 'Monitor on-field location, route timeline, and day-wise travel for every fleet user.' },
    { step: '05', icon: 'ChartBar', title: 'Review & Report', description: 'Attendance, leave, TADA, productivity, and collection reports on the distributor panel.' },
  ],
  featureGroups: [
    {
      label: 'Tasks & Activity',
      features: [
        { icon: 'Package', title: 'Order Tasks', benefit: 'Assign and track order-related delivery tasks for your fleet employees.' },
        { icon: 'HandCoins', title: 'Collection Tasks', benefit: 'Assign outstanding and payment collection tasks with field logging.' },
        { icon: 'Phone', title: 'Calling Tasks', benefit: 'Manage call activity and follow-ups linked to parties and routes.' },
        { icon: 'MapTrifold', title: 'Activity Routes', benefit: 'Route timeline and map view of salesman and rider field activity.' },
        { icon: 'MapPin', title: 'Live Location', benefit: 'Current on-field location of fleet users during the workday.' },
        { icon: 'Path', title: 'Day-Wise Travel', benefit: 'Complete travel history and productivity tracking per employee per day.' },
      ],
    },
    {
      label: 'HR & Workforce',
      features: [
        { icon: 'CalendarCheck', title: 'Attendance', benefit: 'Fleet users mark attendance from the app; rules and reports on the panel.' },
        { icon: 'CalendarBlank', title: 'Leave Management', benefit: 'Apply, approve, and track leave for field employees.' },
        { icon: 'Wallet', title: 'TADA & Payroll', benefit: 'TADA policies, payroll management, and related HR workflows.' },
        { icon: 'Receipt', title: 'Daily Expenses', benefit: 'Field expense submission and approval.' },
        { icon: 'Users', title: 'Employee Management', benefit: 'Manage fleet users linked to your distributor account.' },
        { icon: 'Gear', title: 'Attendance Rules', benefit: 'Configure attendance policies for your field team.' },
      ],
    },
    {
      label: 'Collections & Sync',
      features: [
        { icon: 'ChartLine', title: 'Outstandings', benefit: 'Outstanding master synced and used for collection task assignment.' },
        { icon: 'Vault', title: 'Cash Custody', benefit: 'Cash collection and custody tracking across field and panel.' },
        { icon: 'ArrowsClockwise', title: 'Accounting Sync', benefit: 'ZeroMDS and compatible software — parties, products, orders, outstandings.' },
        { icon: 'Database', title: 'Product & Party Masters', benefit: 'Keep distributor masters aligned with your accounting system.' },
        { icon: 'FileText', title: 'Receipts', benefit: 'Collection and receipt activity logged against parties.' },
        { icon: 'ChartBar', title: 'Reporting', benefit: 'Fleet reporting center with KPIs, productivity, and activity analytics.' },
      ],
    },
  ],
  benefits: [
    'Full control over your own salesmen, riders, and field staff',
    'Order, collection, and calling tasks in one system',
    'Live location plus day-wise travel and activity history',
    'Attendance, leave, TADA, and payroll on the distributor panel',
    'Accounting sync with ZeroMDS and compatible software',
  ],
  faq: [
    {
      question: 'Who uses the fleet app?',
      answer: 'Your distributor employees — salesmen, riders, and field staff. They use the fleet mobile app for attendance, tasks, deliveries, collections, and activity tracking.',
    },
    {
      question: 'What tasks can I assign?',
      answer: 'Order tasks, collection tasks (outstanding/COD), and calling tasks. All activity is tracked from the distributor panel with live location and route history.',
    },
    {
      question: 'Can I see where my team is right now?',
      answer: 'Yes. Fleet SaaS includes live on-field location, activity routes, and complete day-wise travel history for fleet users.',
    },
    {
      question: 'Does it include attendance and leave?',
      answer: 'Yes. Attendance, attendance rules, leave management, TADA, payroll, and daily expenses are part of the Fleet SaaS distributor panel.',
    },
    {
      question: 'Is Fleet SaaS available in my city?',
      answer: 'Yes. Fleet SaaS is available pan-India for medicine distributors. You can onboard from any city in India. Managed logistics delivery is separate and live in Ahmedabad, Surat, Pune, and Mumbai.',
    },
    {
      question: 'Is this different from Logistics?',
      answer: 'Yes. Fleet SaaS is software to manage your own employees. Logistics is our managed delivery service where FastWhistle riders deliver your parcels. You can use either or both.',
    },
  ],
};

export const homeProducts = [
  {
    ...logisticsProduct,
    cardTitle: 'Logistics',
    cardSubtitle: 'Managed delivery service',
    cardDescription: 'Hand parcels to FastWhistle. Our riders deliver same-day — per parcel pricing, real-time tracking, no fleet to manage.',
    cta: 'Explore Logistics',
  },
  {
    ...fleetProduct,
    cardTitle: 'Fleet SaaS',
    cardSubtitle: 'Manage your own team',
    cardDescription: 'Software for your employees — tasks, live tracking, collections, attendance, leave, TADA, and accounting sync.',
    cta: 'Explore Fleet SaaS',
  },
];
